import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  ArrowUpRight,
  Calendar,
  CaretDown,
  Clock,
  FirstAidKit,
  Globe,
  PencilSimpleLine,
  Timer,
} from "@phosphor-icons/react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SessionBout {
  /** Display label, e.g. "Bout 1" */
  label: string;
  /** Duration value, e.g. "36.6s" */
  value: string;
}

export interface SessionDurationSummary {
  /** Total duration, e.g. "205.0s" */
  total: string;
  /** Mean bout duration, e.g. "34.2s" */
  meanBout: string;
  /** Longest bout duration, e.g. "38.0s" */
  longestBout: string;
  /** Number of bouts */
  boutCount: number;
  /** Percentage of session, e.g. "5.7%" */
  percentOfSession: string;
}

export interface SessionMetrics {
  percentCorrect?: number;
  percentIndependent?: number;
  trialsToCriterion?: number;
  promptLevelDistribution?: Record<string, number>;
  countPerSession?: number;
  percentStepsCorrect?: number;
  percentStepsIndependent?: number;
  chainCompletionRate?: number;
  boutCount?: number;
  totalDurationMs?: number;
  meanDurationMs?: number;
  meanLatencyMs?: number;
  latencyByPromptLevel?: Record<string, number>;
  meanRating?: number;
}

export interface AppointmentDetails {
  /** Formatted appointment date, e.g. "August 8th, 2023" */
  dateLabel: string;
  /** Formatted duration, e.g. "75 minutes" */
  durationLabel: string;
  /** Formatted time range with timezone, e.g. "10:03 am - 11:18 am (PST)". Omit to hide the row. */
  timeRangeLabel?: string;
  /** Diagnosis code + description, e.g. "F84 Autism Spectrum Disorder" */
  diagnosisLabel?: string;
  /** Delivery method, e.g. "Telehealth" */
  deliveryMethod?: string;
  /**
   * Most recent billable_time id for this appointment — used to open Billing's
   * session note (`/billing?billableTimeId=`) via `onOpenAppointmentDetails`.
   * Only reachable for telehealth appointments — undefined for in-person.
   */
  billableTimeId?: number;
}

export interface SessionEntry {
  /** Unique session id */
  id: string;
  /** Formatted date label, e.g. "March 29th" */
  dateLabel: string;
  /** Session label, e.g. "Session 24" */
  sessionLabel: string;
  /** Clinician avatar URL */
  avatarUrl?: string;
  /** Clinician name for avatar alt text */
  clinicianName?: string;
  /** L4 primitive type used to determine metric rendering */
  primitiveType?: string | null;
  /** Raw data points from target_session_data.data_point */
  dataPoints?: Array<Record<string, unknown>>;
  /** Derived session metrics */
  metrics?: SessionMetrics;
  /** Duration summary stats — if undefined, details section is hidden */
  durationSummary?: SessionDurationSummary;
  /** Individual bout data */
  bouts?: SessionBout[];
  /** CentralReach clinical session id — used to open the data sheet */
  clinicalSessionId?: string;
  /**
   * Raw CentralReach appointment id for this session's linked appointment
   * (undefined for ad hoc sessions). Used by `onOpenAppointmentDetails` to
   * build the CR deep link — kept separate from `appointmentDetails` since
   * that's display-formatted metadata, not an identifier.
   */
  appointmentId?: number;
  /**
   * Linked therapy appointment metadata. Absent for ad hoc sessions
   * (sessions recorded without an appointment) — the "Appointment Details"
   * block only renders when this is present.
   */
  appointmentDetails?: AppointmentDetails;
}

export interface SessionHistoryProps {
  /** Total session count */
  sessionCount: number;
  /** Last session date label, e.g. "Today" or "March 29th" */
  lastSessionLabel: string;
  /** List of session entries to display in the accordion */
  sessions: SessionEntry[];
  /** Called when a session's data-sheet edit icon is clicked — receives the session id */
  onOpenDataSheet?: (sessionId: string) => void;
  /** Called when a session's "Appointment Details" open icon is clicked */
  onOpenAppointmentDetails?: (session: SessionEntry) => void;
  /**
   * Called when "Record New Session" is clicked, to start an ad hoc Data
   * Sheets session (not tied to an appointment). Button is hidden when
   * omitted.
   */
  onRecordNewSession?: () => void;
  /**
   * `id` of the session to highlight (background tint) — mirrors the chart
   * point under the pointer. `null`/absent leaves every row unhighlighted.
   */
  highlightedSessionId?: string | null;
  /** Additional MUI sx overrides */
  sx?: SxProps<Theme>;
}

// ---------------------------------------------------------------------------
// Internal sub-components
// ---------------------------------------------------------------------------

/** Label–value pair used in the header summary and duration detail rows */
const StatPair: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Typography variant="caption" color="grey.600">
      {label}
    </Typography>
    <Typography variant="caption" color="text.primary">
      {value}
    </Typography>
  </Stack>
);

/** Icon + label pair used in the Appointment Details meta row */
const AppointmentMeta: React.FC<{ icon: React.ReactNode; label: string; testId?: string }> = ({
  icon,
  label,
  testId,
}) => (
  <Stack direction="row" spacing={2} alignItems="center" data-testid={testId}>
    <Box sx={{ color: "grey.600", display: "flex" }}>{icon}</Box>
    <Typography variant="caption" color="text.primary">
      {label}
    </Typography>
  </Stack>
);

/**
 * Shows the linked therapy appointment's date, duration, time range,
 * diagnosis, and delivery method. Only rendered when a session carries
 * `appointmentDetails` — ad hoc sessions have none, per design.
 */
const AppointmentDetailsCard: React.FC<{
  details: AppointmentDetails;
  onOpen?: () => void;
}> = ({ details, onOpen }) => (
  <Box
    sx={{
      bgcolor: "grey.50",
      borderRadius: 3,
      p: 5,
      display: "flex",
      flexDirection: "column",
      gap: 2,
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">
        Appointment Details
      </Typography>
      {onOpen && (
        <IconButton size="small" onClick={onOpen} aria-label="Open appointment billing details">
          <ArrowUpRight size={20} />
        </IconButton>
      )}
    </Stack>
    <Stack direction="row" flexWrap="wrap" columnGap={4} rowGap={2}>
      <AppointmentMeta icon={<Calendar size={16} weight="duotone" />} label={details.dateLabel} />
      <AppointmentMeta icon={<Timer size={16} weight="duotone" />} label={details.durationLabel} />
      {details.timeRangeLabel && (
        <AppointmentMeta
          icon={<Clock size={16} weight="duotone" />}
          label={details.timeRangeLabel}
          testId="appointment-time-range"
        />
      )}
      {details.diagnosisLabel && (
        <AppointmentMeta icon={<FirstAidKit size={16} weight="duotone" />} label={details.diagnosisLabel} />
      )}
      {details.deliveryMethod && (
        <AppointmentMeta icon={<Globe size={16} weight="duotone" />} label={details.deliveryMethod} />
      )}
    </Stack>
  </Box>
);

/** Section title with an optional edit icon that opens the data sheet */
const SectionHeader: React.FC<{ title: string; onEdit?: () => void }> = ({ title, onEdit }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between">
    <Typography variant="body2" color="text.primary">
      {title}
    </Typography>
    {onEdit && (
      <IconButton size="small" onClick={onEdit} aria-label={`Open data sheet for ${title}`}>
        <PencilSimpleLine size={20} />
      </IconButton>
    )}
  </Stack>
);

type PillTone = "purple" | "green" | "red" | "grey" | "yellow";

type DetailPill = {
  id: string;
  text: string;
  tone: PillTone;
};

type DetailSection = {
  title: string;
  chains?: Array<{ label: string; pills: DetailPill[] }>;
  pills?: DetailPill[];
  summary: Array<{ label: string; value: string | number }>;
};

const PILL_STYLES: Record<PillTone, { bg: string; text: string }> = {
  purple: { bg: "#EDE2FA", text: "#7B3FD6" },
  green: { bg: "#CDECCB", text: "#2E7D32" },
  red: { bg: "#F7CDD4", text: "#D62F4E" },
  grey: { bg: "#E6EAF0", text: "#64748B" },
  yellow: { bg: "#F9E7BC", text: "#B86A00" },
};

/**
 * Limit visible occurrence pills so high backend counts do not flood the UI;
 * any remaining occurrences are rolled into one final overflow pill.
 */
const MAX_COUNTER_OCCURRENCE_PILLS = 50;

const Pill: React.FC<{ pill: DetailPill }> = ({ pill }) => (
  <Box
    sx={{
      px: 3,
      py: 1,
      borderRadius: 999,
      bgcolor: PILL_STYLES[pill.tone].bg,
      color: PILL_STYLES[pill.tone].text,
      fontSize: 14,
      lineHeight: 1.43,
      fontWeight: 400,
      fontFamily: "BasierCircle, sans-serif",
      whiteSpace: "nowrap",
      display: "inline-flex",
      alignItems: "center",
      minHeight: 28,
    }}
  >
    {pill.text}
  </Box>
);

/**
 * Accept numeric payloads and numeric strings because session history data
 * points may arrive from the API in either form.
 */
function toNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function toneForPrompt(promptLevel: string): PillTone {
  const normalized = promptLevel.trim().toUpperCase();
  if (normalized === "I") return "green";
  if (normalized === "NR") return "red";
  if (normalized === "--") return "grey";
  return "purple";
}

function toneForCorrect(value: boolean): PillTone {
  return value ? "green" : "red";
}

function toneForRating(value: number): PillTone {
  if (value <= 2) return "red";
  if (value === 3) return "yellow";
  return "green";
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatRatioFromPercent(percent: number, total: number): string {
  if (!Number.isFinite(total) || total <= 0) return `${Math.round(percent)}%`;
  const count = Math.round((percent / 100) * total);
  return `${count}/${total} (${Math.round(percent)}%)`;
}

function formatSeconds(ms: number): string {
  return `${(ms / 1000).toFixed(1)}s`;
}

function buildSection(
  primitiveType: string | null | undefined,
  dataPoints: Array<Record<string, unknown>>,
  metrics?: SessionMetrics
): DetailSection | null {
  if (!primitiveType) return null;

  if (primitiveType === "task_analysis") {
    const hasAnyPromptLevel = dataPoints.some(
      (point) => typeof point.promptLevel === "string" && point.promptLevel.trim() !== ""
    );
    const chains: Array<{ label: string; pills: DetailPill[] }> = [];
    let currentChain: DetailPill[] = [];
    let chainIndex = 1;

    dataPoints.forEach((point, idx) => {
      const stepId = toNumber(point.stepId) ?? idx + 1;
      if (stepId === 1 && currentChain.length > 0) {
        chains.push({ label: `Chain ${chainIndex}:`, pills: currentChain });
        chainIndex += 1;
        currentChain = [];
      }

      const promptLevel = typeof point.promptLevel === "string" ? point.promptLevel : undefined;
      const isCorrect = typeof point.isCorrect === "boolean" ? point.isCorrect : undefined;

      let pillText: string;
      let pillTone: PillTone;

      // When any prompt-level data exists for the session, prefer prompt levels
      // and do not fall back to Correct/Incorrect tokens.
      if (hasAnyPromptLevel) {
        const token = promptLevel ?? "--";
        pillText = `S${stepId}:${token}`;
        pillTone = toneForPrompt(token);
      } else if (isCorrect !== undefined) {
        // For task-analysis when no prompt levels exist, show explicit
        // "Correct"/"Incorrect" labels (and use the correct tone).
        pillText = `S${stepId}:${isCorrect ? "Correct" : "Incorrect"}`;
        pillTone = toneForCorrect(isCorrect);
      } else {
        const token = promptLevel ?? "--";
        pillText = `S${stepId}:${token}`;
        pillTone = toneForPrompt(token);
      }

      currentChain.push({
        id: `ta-${idx}`,
        text: pillText,
        tone: pillTone,
      });
    });

    if (currentChain.length > 0) {
      chains.push({ label: `Chain ${chainIndex}:`, pills: currentChain });
    }

    const totalSteps = dataPoints.length;
    const independentSteps = dataPoints.filter((point) => {
      const promptLevel = typeof point.promptLevel === "string" ? point.promptLevel : "";
      const isCorrect = typeof point.isCorrect === "boolean" ? point.isCorrect : false;
      // If prompt-level data exists for the session, only count 'I' prompt levels
      // as independent. Otherwise fall back to Correct/Incorrect.
      if (hasAnyPromptLevel) return promptLevel.toUpperCase() === "I";
      return promptLevel.toUpperCase() === "I" || (promptLevel === "" && isCorrect);
    }).length;

    const promptedSteps = dataPoints.filter((point) => {
      const promptLevel =
        typeof point.promptLevel === "string" ? point.promptLevel.toUpperCase() : "";
      return (
        promptLevel !== "" && promptLevel !== "I" && promptLevel !== "NR" && promptLevel !== "--"
      );
    }).length;

    return {
      title: "Step-by-Step Breakdown",
      chains,
      summary: [
        { label: "Independent", value: totalSteps > 0 ? `${independentSteps}/${totalSteps}` : "-" },
        { label: "Prompted", value: totalSteps > 0 ? `${promptedSteps}/${totalSteps}` : "-" },
        {
          label: "Chain complete",
          value:
            metrics?.chainCompletionRate !== undefined
              ? metrics.chainCompletionRate > 0
                ? "Yes"
                : "No"
              : "Yes/No",
        },
      ],
    };
  }

  if (primitiveType === "timer") {
    const durations = dataPoints
      .map((point) => {
        const start = toNumber(point.startTime);
        const end = toNumber(point.endTime);
        if (start === undefined || end === undefined || end < start) return undefined;
        return end - start;
      })
      .filter((value): value is number => value !== undefined);

    return {
      title: "Duration Summary",
      pills: durations.map((duration, idx) => ({
        id: `dur-${idx}`,
        text: formatSeconds(duration),
        tone: "purple",
      })),
      summary: [
        { label: "Bout Count", value: metrics?.boutCount ?? durations.length },
        {
          label: "Average",
          value:
            metrics?.meanDurationMs !== undefined ? formatSeconds(metrics.meanDurationMs) : "-",
        },
        {
          label: "Longest bout",
          value: durations.length > 0 ? formatSeconds(Math.max(...durations)) : "-",
        },
      ],
    };
  }

  if (primitiveType.startsWith("latency")) {
    const trialPills: DetailPill[] = dataPoints
      .map((point, idx) => {
        const cue = toNumber(point.cueTime);
        const response = toNumber(point.responseTime);
        if (cue === undefined || response === undefined || response < cue) return null;
        const promptLevel = typeof point.promptLevel === "string" ? point.promptLevel : "";
        const token = promptLevel ? ` ${promptLevel}` : "";

        // If no prompt-level token is present, show latency/duration pills in
        // the purple tone (these are duration-like metrics), otherwise use
        // the prompt-level tone mapping.
        const pillTone: PillTone = promptLevel ? toneForPrompt(promptLevel) : "purple";

        return {
          id: `lat-${idx}`,
          text: `${formatSeconds(response - cue)}${token}`,
          tone: pillTone,
        };
      })
      .filter((pill): pill is DetailPill => pill !== null);

    const prompted = trialPills.filter((pill) => {
      const upper = pill.text.toUpperCase();
      if (!upper.includes(" ")) return false; // No prompt-level token
      return !upper.endsWith(" I") && !upper.endsWith(" NR") && !upper.endsWith(" --");
    }).length;

    return {
      title: "Trial Summary",
      pills: trialPills,
      summary: [
        ...(metrics?.percentIndependent !== undefined
          ? [
              {
                label: "% Independent",
                value: formatPercent(metrics.percentIndependent),
              },
            ]
          : []),
        { label: "Trials", value: metrics?.trialsToCriterion ?? trialPills.length },
        ...(metrics?.percentCorrect !== undefined
          ? [
              {
                label: "Correct",
                value:
                  (metrics?.trialsToCriterion ?? trialPills.length) > 0
                    ? Math.round(
                        (metrics.percentCorrect / 100) *
                          (metrics.trialsToCriterion ?? trialPills.length)
                      )
                    : "-",
              },
            ]
          : []),
        { label: "Prompted", value: prompted },
      ],
    };
  }

  if (primitiveType === "rating_scale") {
    const ratings = dataPoints
      .map((point) => toNumber(point.rating))
      .filter((value): value is number => value !== undefined);

    return {
      title: "Rating Summary",
      pills: ratings.map((rating, idx) => ({
        id: `rt-${idx}`,
        text: `${rating}/5`,
        tone: toneForRating(rating),
      })),
      summary: [
        {
          label: "Mean",
          value: metrics?.meanRating !== undefined ? `${metrics.meanRating.toFixed(1)}/5` : "-",
        },
        { label: "Min", value: ratings.length > 0 ? Math.min(...ratings) : "-" },
        { label: "Max", value: ratings.length > 0 ? Math.max(...ratings) : "-" },
        { label: "Ratings", value: ratings.length },
      ],
    };
  }

  if (primitiveType.startsWith("counter_")) {
    const countPills: DetailPill[] = [];
    const promptPills: DetailPill[] = [];
    const correctPills: DetailPill[] = [];
    dataPoints.forEach((point, idx) => {
      const count = toNumber(point.count);
      if (primitiveType === "counter_occurrence") {
        if (count !== undefined && count === 0) {
          countPills.push({ id: `ct-${idx}`, text: "+0", tone: "purple" });
        } else if (count !== undefined && count > 0) {
          const occurrences = Math.trunc(count);
          const visibleOccurrences = Math.min(occurrences, MAX_COUNTER_OCCURRENCE_PILLS);

          for (
            let occurrenceIndex = 0;
            occurrenceIndex < visibleOccurrences;
            occurrenceIndex += 1
          ) {
            countPills.push({
              id: `ct-${idx}-${occurrenceIndex}`,
              text: "+1",
              tone: "purple",
            });
          }

          const remainingOccurrences = occurrences - visibleOccurrences;
          if (remainingOccurrences > 0) {
            countPills.push({
              id: `ct-${idx}-remaining`,
              text: `+${remainingOccurrences}`,
              tone: "purple",
            });
          }
        }
      } else if (count !== undefined) {
        countPills.push({ id: `ct-${idx}`, text: `+${count}`, tone: "purple" });
      }
      const promptLevel = typeof point.promptLevel === "string" ? point.promptLevel : undefined;
      if (promptLevel) {
        promptPills.push({ id: `pr-${idx}`, text: promptLevel, tone: toneForPrompt(promptLevel) });
      }

      const isCorrect = typeof point.isCorrect === "boolean" ? point.isCorrect : undefined;
      if (isCorrect !== undefined) {
        correctPills.push({
          id: `cr-${idx}`,
          text: isCorrect ? "Correct" : "Incorrect",
          tone: toneForCorrect(isCorrect),
        });
      }
    });

    // If any prompt-level pills exist, show only prompt-level pills (and counts).
    const hasPrompt = promptPills.length > 0;
    const allPills = hasPrompt ? [...countPills, ...promptPills] : [...countPills, ...correctPills];

    return {
      title: "Frequency Summary",
      pills: allPills,
      summary: [
        // Backend controls which summary metrics are valid for the target's
        // primitive/config. Frontend renders only metrics that are present.
        ...(metrics?.percentCorrect !== undefined
          ? [
              {
                label: "Correct",
                value: formatRatioFromPercent(
                  metrics.percentCorrect,
                  metrics?.trialsToCriterion ?? 0
                ),
              },
            ]
          : []),
        ...(metrics?.percentIndependent !== undefined
          ? [
              {
                label: "Independent",
                value: formatRatioFromPercent(
                  metrics.percentIndependent,
                  metrics?.trialsToCriterion ?? 0
                ),
              },
            ]
          : []),
      ],
    };
  }

  return null;
}

/** Expanded accordion content — duration stats + individual bouts */
const SessionDetail: React.FC<{
  primitiveType?: string | null;
  dataPoints?: Array<Record<string, unknown>>;
  metrics?: SessionMetrics;
  summary?: SessionDurationSummary;
  bouts?: SessionBout[];
  onEdit?: () => void;
}> = ({ primitiveType, dataPoints = [], metrics, summary, bouts, onEdit }) => {
  const hasLegacyDetails = Boolean(summary || (bouts && bouts.length > 0));
  const hasMetrics = Boolean(metrics || dataPoints.length > 0);
  if (!hasLegacyDetails && !hasMetrics) return null;

  const section = buildSection(primitiveType, dataPoints, metrics);

  return (
    <Box
      sx={{
        bgcolor: "grey.50",
        borderRadius: 3,
        p: 5,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {section && (
        <>
          <SectionHeader title={section.title} onEdit={onEdit} />

          {section.chains?.map((chain) => (
            <Stack key={chain.label} spacing={1}>
              <Typography variant="caption" color="text.primary">
                {chain.label}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {chain.pills.map((pill) => (
                  <Pill key={pill.id} pill={pill} />
                ))}
              </Stack>
            </Stack>
          ))}

          {section.pills && section.pills.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {section.pills.map((pill) => (
                <Pill key={pill.id} pill={pill} />
              ))}
            </Stack>
          )}

          <Stack direction="row" flexWrap="wrap" gap={4} alignItems="flex-start">
            {section.summary.map((item) => (
              <StatPair key={item.label} label={`${item.label}:`} value={item.value} />
            ))}
          </Stack>
        </>
      )}

      {!section && summary && (
        <>
          <SectionHeader title="Duration Summary" onEdit={onEdit} />
          <Stack direction="row" flexWrap="wrap" gap={6} alignItems="flex-start">
            <StatPair label="Total:" value={summary.total} />
            <StatPair label="Mean bout:" value={summary.meanBout} />
            <StatPair label="Longest bout:" value={summary.longestBout} />
            <StatPair label="Bout count:" value={summary.boutCount} />
            <StatPair label="% of session:" value={summary.percentOfSession} />
          </Stack>
        </>
      )}

      {(section || summary) && bouts && bouts.length > 0 && <Divider />}

      {bouts && bouts.length > 0 && (
        <Stack spacing={1}>
          <Typography variant="caption" color="grey.600">
            Individual Bouts
          </Typography>
          <Stack spacing={1}>
            {bouts.map((bout) => (
              <Stack key={bout.label} direction="row" spacing={6} alignItems="center">
                <Typography variant="caption" color="grey.600" sx={{ minWidth: 48 }}>
                  {bout.label}
                </Typography>
                <Chip
                  label={bout.value}
                  size="small"
                  sx={{
                    bgcolor: "purple.200",
                    color: "primary.main",
                    fontWeight: 400,
                    fontSize: 12,
                    height: 22,
                    "& .MuiChip-label": { px: "6px", py: "2px" },
                  }}
                />
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// SessionHistory
// ---------------------------------------------------------------------------

/**
 * Displays a chronological list of session entries for a target, each
 * expandable to show duration summary stats and individual bout data.
 *
 * Uses MUI Accordion for the expandable rows with Avatar, date, and
 * session number in the summary.
 *
 * @param props
 * @param props.sessionCount - Total number of sessions
 * @param props.lastSessionLabel - Display label for last session date
 * @param props.sessions - Array of session entries
 * @param [props.highlightedSessionId] - Session id to highlight (chart-hover sync)
 * @param [props.sx] - Additional MUI sx overrides
 */
const SessionHistory: React.FC<SessionHistoryProps> = ({
  sessionCount,
  lastSessionLabel,
  sessions,
  onOpenDataSheet,
  onOpenAppointmentDetails,
  onRecordNewSession,
  highlightedSessionId,
  sx,
}) => {
  return (
    <Box
      component="section"
      aria-label="Session history"
      sx={[
        {
          display: "flex",
          flexDirection: "column",
          gap: 6,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {/* Header */}
      <Stack spacing={2}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Stack spacing={0}>
            <Typography variant="body2" color="text.primary">
              Session History
            </Typography>
            <Typography variant="caption" color="grey.600">
              Individual session data and details
            </Typography>
          </Stack>
          {onRecordNewSession && (
            <Button variant="text" size="small" color="primary" onClick={onRecordNewSession}>
              Record New Session
            </Button>
          )}
        </Stack>

        <Stack direction="row" spacing={6}>
          <StatPair label="Sessions:" value={sessionCount} />
          <StatPair label="Last session:" value={lastSessionLabel} />
        </Stack>
      </Stack>

      {/* Session list */}
      <Box
        sx={{
          overflow: "hidden",
        }}
      >

        {/* Accordion list */}
        {sessions.map((session) => (
          <Accordion
            key={session.id}
            data-highlighted={session.id === highlightedSessionId ? "true" : undefined}
            disableGutters
            elevation={0}
            sx={{
              "&::before": { display: "none" },
              transition: "background-color 120ms ease",
              bgcolor: session.id === highlightedSessionId ? "#F6EEFA" : "transparent",
              "& .MuiAccordionDetails-root": {
                px: 0,
                pt: 4,
                pb: 4,
              },
            }}
          >
            <AccordionSummary
              expandIcon={<CaretDown size={20} />}
              sx={{
                px: 0,
                py: 1,
                minHeight: "auto",
                "& .MuiAccordionSummary-content": {
                  my: 1,
                },
              }}
            >
              {/* Own the row spacing here rather than on `.MuiAccordionSummary-content`:
                  the theme's MuiAccordion override sets a gap on that same slot, and
                  the tie is resolved by emotion's injection order — which flips for the
                  highlighted row (its parent Accordion carries a different sx), making
                  the gap render inconsistently. A dedicated Stack sidesteps the tie. */}
              <Stack direction="row" alignItems="center" sx={{ gap: "6px", minWidth: 0 }}>
                <Avatar
                  src={session.avatarUrl}
                  alt={session.clinicianName ?? "Clinician"}
                  sx={{ width: 32, height: 32 }}
                />
                <Typography variant="subtitle2" color="text.primary">
                  {session.dateLabel}
                </Typography>
                <Typography variant="caption" color="grey.600">
                  {session.sessionLabel}
                </Typography>
              </Stack>
            </AccordionSummary>

            {(session.appointmentDetails ||
              (session.dataPoints && session.dataPoints.length > 0) ||
              session.metrics ||
              session.durationSummary ||
              (session.bouts && session.bouts.length > 0)) && (
              <AccordionDetails>
                <Stack spacing={2}>
                  {session.appointmentDetails && (
                    <AppointmentDetailsCard
                      details={session.appointmentDetails}
                      onOpen={
                        onOpenAppointmentDetails && session.appointmentDetails.billableTimeId != null
                          ? () => onOpenAppointmentDetails(session)
                          : undefined
                      }
                    />
                  )}
                  <SessionDetail
                    primitiveType={session.primitiveType}
                    dataPoints={session.dataPoints}
                    metrics={session.metrics}
                    summary={session.durationSummary}
                    bouts={session.bouts}
                    onEdit={
                      onOpenDataSheet && session.clinicalSessionId
                        ? () => onOpenDataSheet(session.clinicalSessionId!)
                        : undefined
                    }
                  />
                </Stack>
              </AccordionDetails>
            )}
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

export default SessionHistory;
